import React from 'react';
import propTypes from 'prop-types';
import { isFunction } from '@dateam/ark';
import {
    Button,
    Form,
    Modal
} from 'shared-ui';
import { useCreateCampaign } from 'data/campaign';
import styles from './CreateCampaignDialog.module.scss';

type Props = {
    className?: string;
    onClose?: () => void;
    onCompletion?: (campainId: number) => void;
};

const CreateCampaignDialog: React.FC<Props> = ({
    className,
    onClose,
    onCompletion
}: Props) => {
    const { mutateAsync: createCampaign, isLoading: isCreating } = useCreateCampaign();
    const [labelState, setLabelState] = React.useState<App.CreateCampaignRequest['label']>('');

    const formIsValid = React.useMemo(() => labelState.trim().length > 0, [labelState]);

    const handleLabelChange = React.useCallback((value: string) => {
        setLabelState(value);
    }, []);

    const handleCloseClick = React.useCallback(() => {
        if (isFunction(onClose)) {
            onClose();
        }
    }, [onClose]);

    const handleCreateClick = React.useCallback(async () => {
        if (!formIsValid) return;

        const newCampaignId = await createCampaign({ label: labelState });

        if (isFunction(onCompletion)) {
            onCompletion(newCampaignId);
        }
    }, [labelState, formIsValid, createCampaign, onCompletion]);

    return (
        <Modal
            className={`${styles['modal']} modal-flex`}
            backdrop
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    Créer une nouvelle tournée
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Form.Group controlId="campaign-name" className="col">
                        <Form.Label>Nom de la tournée</Form.Label>
                        <Form.Input
                            value={labelState}
                            maxlength={50}
                            autocomplete="off"
                            readOnly={isCreating}
                            onChange={handleLabelChange}
                        />
                    </Form.Group>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    disabled={isCreating}
                    onClick={handleCloseClick}
                >
                    Annuler
                </Button>
                <Button
                    color="primary"
                    disabled={!formIsValid}
                    pending={isCreating}
                    onClick={handleCreateClick}
                >
                    Créer la tournée
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

CreateCampaignDialog.propTypes = {
    className: propTypes.string,
    onClose: propTypes.func,
    onCompletion: propTypes.func
};

CreateCampaignDialog.defaultProps = {
    className: undefined,
    onClose: undefined,
    onCompletion: undefined
};

export default CreateCampaignDialog;
